import * as _ from "lodash-es";
import React, { useContext, useEffect, useState } from "react";
import "./OrderEntryFloatingSubmit.scss";
import IOrder from "../../../models/IOrder";
import IOrder_Progress_State from "../../../models/IOrder_Progress_State";
import OrderStatesEnum from "../../../models/OrderStatesEnum";
import OrderEntryService from "../../../services/OrderEntryService";
import OwButton from "../../shared/OwButton/OwButton";
import OwTranslate from "../../shared/OwTranslate/OwTranslate";
import ISubmitOrderDTO from "../../../models/DTO/ISubmitOrderDTO";
import { NumericFormat } from "react-number-format";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import UiContext from "../../../contexts/UiContext";

type Props = {
    submitFn: (payload: ISubmitOrderDTO) => void;
    order: IOrder;
    hasPendingDetailChanges: boolean;
    btnActive: boolean;
    hasEditMode: boolean;
    isFineDetailsValid: boolean;
    hasPendingFineDetailChanges: boolean;
    hasRowsInEditMode: boolean;
    convertQuoteToOrderActive: boolean;
    hideSubmitBtn?: boolean;
};

export const OrderEntryFloatingSubmit: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const uiContext = useContext(UiContext);

    // const destroy$: Subject<any> = new Subject<any>();

    const [isSideBarOpen, setIsSideBarOpen] = useState(uiContext.getSidebarState());
    const [isVisible, setIsVisible] = useState(true);
    const [menuState, setMenuState] = useState({
        isMenuOpen: uiContext.isMenuOpen(),
        isMenuIconMode: uiContext.isMenuIconMode(),
        lookupWidth: "",
        lookupLeft: "",
    });

    useEffect(() => {
        const { lookupWidth, lookupLeft } = getLookupSize(uiContext.isMenuOpen(), uiContext.isMenuIconMode());

        setMenuState({ isMenuOpen: uiContext.isMenuOpen(), isMenuIconMode: uiContext.isMenuIconMode(), lookupWidth, lookupLeft });
    }, [uiContext.menuIconMode, uiContext.menuOpen]);

    const sideBarOpen = uiContext.getSidebarState();
    const menuOpen = uiContext.isMenuOpen();
    const menuIconMode = uiContext.isMenuIconMode();

    useEffect(() => {
        setIsSideBarOpen(sideBarOpen);
    }, [sideBarOpen]);

    const toggleVisible = () => {
        setIsVisible(!isVisible);
    };

    const getLookupSize = (isMenuOpen?: boolean, isMenuIconMode?: boolean) => {
        let lookupWidth = "100%";
        let lookupLeft = "0px";

        if (_.isNil(isMenuOpen)) {
            isMenuOpen = menuState.isMenuOpen;
        }

        if (_.isNil(isMenuIconMode)) {
            isMenuIconMode = menuState.isMenuIconMode;
        }

        const mainView = document.getElementById("mainView");

        if (mainView && (isMenuOpen || isMenuIconMode)) {
            const padding = 0;
            lookupWidth = `${mainView.offsetWidth}px`;
            lookupLeft = `${mainView.offsetLeft + padding}px`;
        }

        return { lookupWidth, lookupLeft };
    };

    const doSubmit = (nextStep?: IOrder_Progress_State) => {
        // send to the next order state.
        if (nextStep) {
            const payload: ISubmitOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                orderGuid: props.order.guid,
                order_state_id: nextStep.order_state_id,
            };

            props.submitFn(payload);
        }
    };

    const getCurrentAndNextOrderStep = (): {
        currentStep: IOrder_Progress_State | undefined;
        nextStep: IOrder_Progress_State | undefined;
    } => {
        return OrderEntryService.getCurrentAndNextOrderSteps(props.order, supplierModel);
    };

    const { currentStep, nextStep } = getCurrentAndNextOrderStep();

    let stepButtonDisabledReason: any | null = <div>&nbsp;</div>;

    if (props.hasPendingDetailChanges) {
        stepButtonDisabledReason = <OwTranslate defaultMessage={"Pending Changes"} id={"pending_changes_save_first"} />;
    }

    const allowSubmit = (): boolean => {
        if (props.order) {
            const result = OrderEntryService.allowSubmit(supplierModel, userModel, props.order, props.hasEditMode);
            return result.allowSubmit;
        } else {
            return false;
        }
    };

    let showManualShipBtns = false;

    if (nextStep && nextStep.order_state_id === OrderStatesEnum.SHIPPED) {
        if (!nextStep.autoCreateShipment) {
            showManualShipBtns = true;
        }
    }

    let stepButtonDisabled = false;

    if (props.order.Vendor) {
        if (supplierModel.has_vendor_credit_limits) {
            if ((props.order.Vendor.credit_limit || 0) > 0) {
                const availableBalance = (props.order.Vendor.credit_limit || 0) - (props.order.Vendor.open_balance || 0);

                if (availableBalance < (props.order.grand_total || 0)) {
                    stepButtonDisabled = true;
                    stepButtonDisabledReason = (
                        <OwTranslate
                            defaultMessage={"Not Enough Available Credit: {amount}"}
                            id={"not_enough_available_credit"}
                            values={{
                                amount: (
                                    <NumericFormat
                                        value={availableBalance}
                                        prefix={"$"}
                                        decimalScale={2}
                                        fixedDecimalScale={true}
                                        thousandSeparator={true}
                                        displayType={"text"}
                                    />
                                ),
                            }}
                        />
                    );
                }
            }
        }
    }

    const elem =
        nextStep && props.hasEditMode && allowSubmit() && !showManualShipBtns ? (
            <div className={"OrderEntryFloatingSubmit"}>
                <div
                    className={(isSideBarOpen ? "sideBarOpen" : "") + " FloatingBar " + (isVisible ? "" : "hide-bar")}
                    style={{ width: menuState.lookupWidth }}>
                    {props.hideSubmitBtn ? null : (
                        <div className={"toggle-row"}>
                            <i className={"fas " + (isVisible ? "fa-chevron-down" : "fa-chevron-up")} onClick={toggleVisible} />
                        </div>
                    )}
                    {stepButtonDisabledReason.length ? (
                        <div className={"text-danger text-center feedback-row"}>{stepButtonDisabledReason}</div>
                    ) : null}

                    <div className={"text-white text-center"}>
                        {props.order?.Vendor?.name} - {props.order.name}
                    </div>

                    {props.hideSubmitBtn ? null : (
                        <div className={"d-flex justify-content-center align-items-center mt-2 button-row"}>
                            <OwButton
                                btnSizeClass={"btn-sm"}
                                className={"btn btn-warning"}
                                title={"submit"}
                                leftIconClass={"fas fa-arrow-circle-right"}
                                disabled={
                                    props.hasPendingDetailChanges ||
                                    props.hasPendingFineDetailChanges ||
                                    props.hasRowsInEditMode ||
                                    !props.isFineDetailsValid ||
                                    props.convertQuoteToOrderActive ||
                                    stepButtonDisabled
                                }
                                fn={() => {
                                    doSubmit(nextStep);
                                }}
                                active={props.btnActive}
                            />
                        </div>
                    )}
                </div>
            </div>
        ) : null;

    return elem;
};
